import { useEffect, useId, useState, type FormEvent } from "react";
import { isConflict } from "../../api/errors";
import { useRunAgentSessionTask } from "../../api/queries";
import type { AgentSession } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface RunTaskDialogProps {
  session: AgentSession | null;
  onClose: () => void;
}

/**
 * Sends a task prompt to a Bonnie session's agent. 202 is "queued", not
 * "done" — progress shows up back on the dashboard row (branch/dirty/
 * changeSummary), not in this dialog, so a successful submit just confirms
 * and closes rather than pretending to show live agent output (no
 * reattachable terminal yet — ADR-0039 §"Deferred").
 */
export function RunTaskDialog({ session, onClose }: RunTaskDialogProps) {
  const promptId = useId();
  const { pushToast } = useToast();
  const mutation = useRunAgentSessionTask();
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (session) {
      setPrompt("");
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  function handleClose() {
    setPrompt("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) return;
    const submittedPrompt = prompt.trim();
    mutation.mutate(
      { id: session.id, input: { prompt: submittedPrompt } },
      {
        onSuccess: () => {
          pushToast({ title: `Task sent to "${session.label}".`, tone: "success" });
          handleClose();
        },
      },
    );
  }

  return (
    <Modal open={session !== null} title="Run a task" onClose={handleClose} initialFocusId={promptId}>
      {session && (
        <form onSubmit={handleSubmit} noValidate>
          <p>
            Send a task to <strong>{session.label}</strong>
            {session.branch ? (
              <>
                {" "}
                on <code>{session.branch}</code>
              </>
            ) : null}
            .
          </p>

          {session.status !== "running" && (
            <Banner tone="warning" title="Session isn't running" className="bonnie-dialog-banner">
              Only a running session can accept a task. Start "{session.label}" first.
            </Banner>
          )}

          <label className="form-field" htmlFor={promptId}>
            <span>Task prompt</span>
            <textarea
              id={promptId}
              rows={5}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              required
              maxLength={8000}
              placeholder="e.g. Add input validation to the signup form and cover it with a test."
            />
          </label>

          {mutation.isError && mutation.error && (() => {
            const err = mutation.error;
            const message = err.message || "Couldn't send that task.";
            // isConflict (409) means the session isn't running — an
            // informational refusal, not the operator's mistake (same
            // posture as ServiceDetailPage's deploy-409 handling), so it
            // gets a neutral banner rather than the alarming error text.
            return isConflict(err) ? (
              <Banner tone="warning" title="Session not running" className="bonnie-dialog-banner">
                {message}
              </Banner>
            ) : (
              <FormError>{message}</FormError>
            );
          })()}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={mutation.isPending || !prompt.trim()}>
              {mutation.isPending ? "Sending…" : "Send task"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
